import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '@/hooks/useAuth';
import { usePedidoStore, podeRecuperarDraft } from '../stores/pedido.store';
import { PedidoWizard } from '../components/wizard/PedidoWizard';

// AIDEV-NOTE: Pagina que renderiza o PedidoWizard.
// Ao sair sem finalizar, exibe window.confirm para confirmar descarte.
// Ao montar, verifica se ha rascunho no localStorage: se houver itens + pedidoId, oferece
// continuar (Dialog); senao, reseta. Resolve incidente 2026-08 (reset cego + reload apagava
// carrinho do vendedor).

export function NovoPedidoPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const resetPedido = usePedidoStore((s) => s.resetPedido);
  const itens = usePedidoStore((s) => s.itens);
  const [recuperarOpen, setRecuperarOpen] = useState(false);
  const [draftInfo, setDraftInfo] = useState<{
    clienteRazaoSocial: string | null;
    itensCont: number;
  }>({
    clienteRazaoSocial: null,
    itensCont: 0
  });

  // AIDEV-NOTE: Recuperacao de pedido em andamento — o store e persistido (localStorage).
  // Se ha rascunho com itens de uma sessao anterior, oferece continuar em vez de resetar
  // (incidente 2026-08: reset cego + reload apagava o carrinho do vendedor). podeRecuperarDraft
  // (Finding 1 + 3 do review final) filtra dois vazamentos: draft de EDICAO (nunca oferecido aqui —
  // evita autosave 400 em loop contra pedido ja finalizado/erro_sync) e draft de OUTRO vendedor em
  // dispositivo compartilhado. Em ambos os casos, e em qualquer draft invalido, reseta sem dialogo.
  useEffect(() => {
    const {
      itens: itensPersistidos,
      pedidoId,
      clienteSelecionado,
      draftOrigem,
      draftVendedorId
    } = usePedidoStore.getState();
    if (
      podeRecuperarDraft(
        { itens: itensPersistidos, pedidoId, draftOrigem, draftVendedorId },
        user?.vendedor_id
      )
    ) {
      setDraftInfo({
        clienteRazaoSocial: clienteSelecionado?.RAZAO_SOCIAL ?? null,
        itensCont: itensPersistidos.length
      });
      setRecuperarOpen(true);
    } else {
      resetPedido();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // AIDEV-NOTE: Previne saida acidental com itens no carrinho
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (itens.length > 0) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [itens.length]);

  const handleVoltar = useCallback(() => {
    if (itens.length > 0) {
      const confirmar = window.confirm(
        'Voce tem itens no carrinho. Deseja descartar o pedido e voltar?'
      );
      if (!confirmar) return;
    }
    resetPedido();
    navigate('/pedidos');
  }, [itens.length, resetPedido, navigate]);

  return (
    // AIDEV-NOTE: flex: 1 herda a altura do MainLayout (height: calc(100vh - 64px), display: flex).
    // display: flex + flexDirection: column propaga a cadeia de altura ate o PedidoWizard.
    // overflow: hidden aqui — cada componente filho gerencia seu proprio scroll.
    <Box
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
    >
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={handleVoltar} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Novo Pedido
          </Typography>
        </Toolbar>
      </AppBar>

      {/* AIDEV-NOTE: flex: 1 + display: flex permite que PedidoWizard use flex: 1 para preencher */}
      <Box
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
      >
        <PedidoWizard />
      </Box>

      <Dialog open={recuperarOpen} disableEscapeKeyDown>
        <DialogTitle>Pedido em andamento encontrado</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Existe um pedido não finalizado com {draftInfo.itensCont} item(ns)
            {draftInfo.clienteRazaoSocial ? ` para ${draftInfo.clienteRazaoSocial}` : ''}. Deseja
            continuar de onde parou?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={() => {
              resetPedido();
              setRecuperarOpen(false);
            }}
          >
            Descartar
          </Button>
          <Button variant="contained" onClick={() => setRecuperarOpen(false)}>
            Continuar pedido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
